/**
 * RecipientStatusBadge — badge cohérent pour les 6 statuts QuoteRequestRecipient.
 *
 * Mapping enum `app.qrr_status` :
 *   - pending      → slate (en attente d'envoi)
 *   - sent         → amber (email envoyé, en attente réponse)
 *   - viewed       → brand-blue (recipient a ouvert le mail — futur, pas tracé en log mode)
 *   - responded    → emerald (a répondu, soumission reçue)
 *   - declined     → amber (a refusé)
 *   - expired      → slate (deadline dépassée)
 *   - bounced      → red (9.L.5 — email rejeté définitivement, hard bounce)
 *   - complained   → red (9.L.5 — plainte spam FBL)
 *   - unsubscribed → slate (9.L.5 — désabonnement, consent withdrawal Loi 25)
 *
 * Pattern parité `QuoteRequestStatusBadge`. Les 3 statuts feedback de livraison
 * (9.L.5) sont posés par le webhook Mailgun (`/v1/public/webhooks/mailgun`).
 */

import type { QuoteRequestRecipientStatus } from "@kollectivia/shared";
import { useTranslation } from "react-i18next";

type Tone = "slate" | "amber" | "brand" | "emerald" | "red";

const TONE: Record<Tone, { bg: string; text: string; dot: string }> = {
  slate: { bg: "bg-slate-100", text: "text-slate-700", dot: "bg-slate-400" },
  amber: { bg: "bg-amber-50", text: "text-amber-700", dot: "bg-amber-500" },
  brand: { bg: "bg-brand-50", text: "text-brand-700", dot: "bg-brand-500" },
  emerald: {
    bg: "bg-emerald-50",
    text: "text-emerald-700",
    dot: "bg-emerald-500",
  },
  red: { bg: "bg-red-50", text: "text-red-700", dot: "bg-red-500" },
};

function toneFor(status: QuoteRequestRecipientStatus): Tone {
  switch (status) {
    case "pending":
      return "slate";
    case "sent":
      return "amber";
    case "viewed":
      return "brand";
    case "responded":
      return "emerald";
    case "declined":
      return "amber";
    case "expired":
      return "slate";
    // 9.L.5 — feedback de livraison Mailgun
    case "bounced":
      return "red";
    case "complained":
      return "red";
    case "unsubscribed":
      return "slate";
    default:
      return "slate";
  }
}

type Props = {
  status: QuoteRequestRecipientStatus;
  deliveryFeedback?: string | null;
};

export function RecipientStatusBadge({ status, deliveryFeedback }: Props) {
  const { t } = useTranslation();
  const delivered = status === "sent" && deliveryFeedback === "delivered";
  const tone = TONE[delivered ? "emerald" : toneFor(status)];
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full ${tone.bg} px-2 py-0.5 text-[11px] font-medium ${tone.text}`}
    >
      <span aria-hidden="true" className={`h-1.5 w-1.5 rounded-full ${tone.dot}`} />
      {t(delivered ? "flow_clarity.delivered" : `quote_requests.recipient_statuses.${status}`)}
    </span>
  );
}
